<!-- Responsibility: Finalize and launch a new game pairing.
Scope: Owns pairing validation and game initiation triggers. -->
<pairing-view class="menu-section active menu-overlay">
  <h3 class="lobby-section-title">START NEW GAME</h3>
  <p class="lobby-hint">Pair one world with one vessel, then initiate the run.</p>

  <div class="pair-grid">
    <section class="pair-card">
      <span class="pair-kicker">WORLD</span>
      <strong>{ state.selectedWorld ? state.selectedWorld.seedLabel : 'No world selected yet.' }</strong>
      <p>{ state.selectedWorld ? `${state.selectedWorld.roomName} · ${state.selectedWorld.playerIdentity}` : 'Choose a reusable world or forge a new one from World Gallery.' }</p>
      <button class="action-btn" onclick={ () => navigateState('worlds') }>{ state.selectedWorld ? 'CHANGE WORLD' : 'CHOOSE WORLD' }</button>
    </section>

    <section class="pair-card vessel">
      <span class="pair-kicker">VESSEL</span>
      <strong>{ state.selectedVessel ? state.selectedVessel.identity : 'No vessel selected yet.' }</strong>
      <p>{ state.selectedVessel ? state.selectedVessel.bio : 'Choose a compiled vessel profile or forge one after selecting a world.' }</p>
      <button class="action-btn" onclick={ () => navigateState('vessels') }>{ state.selectedVessel ? 'CHANGE VESSEL' : 'CHOOSE VESSEL' }</button>
    </section>
  </div>

  <button class="action-btn primary" disabled={ !canStart() || state.starting } onclick={ startSelectedGame }>
    { state.starting ? 'INITIATING...' : 'START THIS GAME' }
  </button>
  <a class="nav-link" onclick={ goHub }>← BACK TO HUB</a>

  <script>
    import * as gameStore from '../../game-store.js'
    import { navigateState, sysNotify } from '../../app-states.js'
    import { persistenceService } from '../../services/persistence-service.ts'
    import { LOBBY_SELECTION_KEY } from '../lobby/lobby-state.ts'

    export default {
      state: {
        selectedWorld: null,
        selectedVessel: null,
        starting: false,
      },

      onMounted() {
        this.loadData()
      },

      async loadData() {
        try {
          const raw = sessionStorage.getItem(LOBBY_SELECTION_KEY)
          if (!raw) return
          const parsed = JSON.parse(raw)
          
          const worldId = String(parsed?.selectedWorldSaveId || '').trim()
          const vesselKey = String(parsed?.selectedVesselKey || '').trim()

          const [worlds, vessels] = await Promise.all([
            persistenceService.listWorlds(),
            persistenceService.listVessels(),
          ])

          const worldMatch = worlds.find(w => w.saveId === worldId)
          let selectedWorld = worldMatch ? { ...worldMatch, seedLabel: this.worldSeedLabel(worldMatch.seed) } : null
          if (!selectedWorld && gameStore.importedWorldData) {
            selectedWorld = { 
              seedLabel: 'Imported Blueprint',
              roomName: 'Start',
              playerIdentity: 'N/A',
              seed: gameStore.importedWorldData.seed 
            }
          }

          const vesselMatch = vessels.find(v => v.key === vesselKey)
          let selectedVessel = vesselMatch ? vesselMatch : null
          if (!selectedVessel && gameStore.importedPlayerData) {
            selectedVessel = {
              identity: gameStore.importedPlayerData.identity,
              bio: gameStore.importedPlayerData.bio,
              playerData: gameStore.importedPlayerData
            }
          }

          this.update({ selectedWorld, selectedVessel })
        } catch (e) {
          console.error(e)
        }
      },

      worldSeedLabel(seed) {
        const firstLine = String(seed ?? '').split('\n')[0].trim()
        if (!firstLine) return 'UNTITLED WORLD'
        return firstLine.length > 66 ? `${firstLine.slice(0, 66)}...` : firstLine
      },

      canStart() {
        return !!this.state.selectedWorld && !!this.state.selectedVessel
      },

      async startSelectedGame() {
        if (this.state.starting || !this.canStart()) return
        
        this.update({ starting: true })
        sysNotify('Initiating selected reality...')

        try {
          const { selectedWorld, selectedVessel } = this.state
          let worldData = gameStore.importedWorldData
          if (selectedWorld.saveId) {
            worldData = await gameStore.importWorldFromSave(selectedWorld.saveId)
          }
          if (!worldData) throw new Error('Selected world unavailable')
          
          gameStore.setImportedPlayerData(selectedVessel.playerData)
          const result = await gameStore.startNewGame({
            identity: selectedVessel.identity,
            bio: selectedVessel.bio,
            sex: selectedVessel.sex,
            seed: selectedWorld.seed,
            worldData,
            playerData: selectedVessel.playerData,
          })
          
          const saveId = String(result?.saveId || gameStore.activeSaveId || '').trim()
          if (!saveId) throw new Error('Active save id missing after launch')
          
          navigateState('scene', saveId)
        } catch (err) {
          console.error(err)
          sysNotify('Failed to start this pairing.')
          this.update({ starting: false })
        }
      },

      goHub() {
        navigateState('hub')
      },

      navigateState // Expose to template
    }
  </script>

  <style>
    pairing-view {
      display: flex;
      flex-direction: column;
      gap: var(--space-lg);
      width: 100%;
      max-width: min(100%, 78rem);
    }

    .lobby-section-title {
      color: var(--text-muted);
      font-size: var(--type-sm);
      letter-spacing: 0.14em;
      border-bottom: 1px solid var(--border);
      padding-bottom: var(--space-sm);
      margin: 0;
    }

    .lobby-hint {
      color: var(--text-dim);
      font-size: var(--type-md);
      line-height: 1.45;
    }

    .pair-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: var(--space-sm);
      perspective: 87.5rem;
    }

    .pair-card {
      display: grid;
      gap: var(--space-xs);
      padding: clamp(var(--space-sm), 2vw, var(--space-md));
      border-radius: var(--radius-lg);
      border: 1px solid color-mix(in srgb, var(--gold) 16%, transparent);
      background:
        linear-gradient(135deg, color-mix(in srgb, var(--surface) 84%, var(--bg)), color-mix(in srgb, var(--gold) 12%, #03060b)),
        radial-gradient(circle at 16% 16%, color-mix(in srgb, var(--gold) 12%, transparent), transparent 12%);
      box-shadow:
        0 1.375rem 2.5rem rgba(0, 0, 0, 0.22),
        inset 0 1px 0 color-mix(in srgb, var(--text-prose) 12%, transparent);
      transform-style: preserve-3d;
      transition:
        transform 0.24s ease,
        box-shadow 0.24s ease,
        border-color 0.18s ease;
      font-size: var(--type-md);
    }

    .pair-card.vessel {
      border-color: color-mix(in srgb, var(--brand-primary) 16%, transparent);
      background:
        linear-gradient(135deg, color-mix(in srgb, var(--surface) 84%, var(--bg)), color-mix(in srgb, var(--brand-primary) 12%, #03060b)),
        radial-gradient(circle at 84% 16%, color-mix(in srgb, var(--brand-primary) 12%, transparent), transparent 12%);
    }

    .pair-card:hover,
    .pair-card:focus-within {
      transform: translateY(-0.25rem);
      box-shadow:
        0 1.875rem 2.875rem rgba(0, 0, 0, 0.28),
        inset 0 1px 0 color-mix(in srgb, var(--text-prose) 16%, transparent);
    }

    .pair-kicker {
      color: var(--gold);
      font-size: 0.76em;
      letter-spacing: 0.14em;
    }

    .pair-card.vessel .pair-kicker {
      color: color-mix(in srgb, var(--brand-primary) 84%, var(--text-prose));
    }

    .pair-card strong {
      color: var(--text-prose);
      font-size: 1em;
      line-height: 1.3;
    }

    .pair-card p {
      color: var(--text-muted);
      font-size: 0.94em;
      line-height: 1.5;
      min-height: 4.2rem;
      margin: 0;
    }

    @media (max-width: 45rem) {
      .pair-grid {
        grid-template-columns: 1fr;
      }
    }
  </style>
</pairing-view>
